/**
 * CorrectionBreakdown — how often each predicted field had to be fixed.
 *
 * The point of the accuracy tab: the tallest bar is what to fix next.
 * Group-derived bars are suppressed (not shown as 0%) when no row in scope
 * carries the v2 provenance fields, so a missing migration can never read as
 * perfect accuracy.
 */

import { Card } from "@/shared/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { CHART_COLORS } from "@/features/admin/config/dashboardTheme";
import { captured, formatRate, rate } from "../aggregate";
import {
	CORRECTION_FIELDS,
	CORRECTION_LABELS,
	type CorrectionField,
	type MetricsBucket,
} from "../types";

/** Bars that depend on the v2 provenance columns. */
const V2_FIELDS: ReadonlySet<CorrectionField> = new Set([
	"groupMatter",
	"groupMembership",
]);

interface CorrectionBreakdownProps {
	bucket: MetricsBucket;
	/** Rows in scope carrying v2 fields. Zero suppresses the group bars. */
	provenanceCoverage: number;
	/** Rows carrying a value per v1 field. Zero suppresses that field's bar. */
	v1ProvenanceCoverage: Record<CorrectionField, number>;
	/** Human-readable denominator, e.g. "exported + deleted entries". */
	denominatorLabel: string;
}

export function CorrectionBreakdown({
	bucket,
	provenanceCoverage,
	v1ProvenanceCoverage,
	denominatorLabel,
}: CorrectionBreakdownProps) {
	const denominator = captured(bucket, "count");

	const bars = CORRECTION_FIELDS.map((field) => {
		const suppressed = V2_FIELDS.has(field)
			? provenanceCoverage === 0
			: v1ProvenanceCoverage[field] === 0;
		return {
			field,
			label: CORRECTION_LABELS[field],
			count: bucket.corrections[field],
			pct: suppressed ? null : rate(bucket.corrections[field], denominator),
			suppressed,
		};
	}).sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1));

	const missed = bucket.matterProvenance.system_missed;

	return (
		<Card className="p-5">
			<div className="flex items-baseline justify-between gap-3">
				<h3 className="text-sm font-semibold">Corrections by field</h3>
				<span className="text-xs text-muted-foreground">
					of {denominator.toLocaleString()} {denominatorLabel}
				</span>
			</div>

			<div className="mt-4 space-y-2.5">
				{bars.map(({ field, label, count, pct, suppressed }) => (
					<div key={field} className="flex items-center gap-3">
						<span className="w-32 shrink-0 text-xs text-muted-foreground">
							{label}
						</span>
						<div className="h-5 flex-1 overflow-hidden rounded bg-muted">
							{pct !== null && (
								<div
									className="h-full rounded transition-[width] duration-300"
									style={{
										width: `${Math.min(pct, 100)}%`,
										backgroundColor: CHART_COLORS.exported.fill,
									}}
								/>
							)}
						</div>
						{suppressed ? (
							<Tooltip>
								<TooltipTrigger asChild>
									<span className="w-24 shrink-0 cursor-help text-right text-xs text-muted-foreground">
										no data
									</span>
								</TooltipTrigger>
								<TooltipContent className="max-w-xs">
									{V2_FIELDS.has(field)
										? "No entry in this range carries group-provenance data. These columns are written only on edits made after the backend migration, and there is no backfill — showing 0% would read as perfect accuracy."
										: "No entry in this range carries a value for this field, so edits to it cannot be detected. This is not 'no edits were made' — it is 'this source cannot tell us'."}
								</TooltipContent>
							</Tooltip>
						) : (
							<span className="w-24 shrink-0 text-right text-xs tabular-nums">
								{formatRate(pct)}
								<span className="ml-1 text-muted-foreground">({count})</span>
							</span>
						)}
					</div>
				))}
			</div>

			{provenanceCoverage > 0 && missed > 0 && (
				<p className="mt-4 border-t pt-3 text-xs text-muted-foreground">
					<span className="font-medium text-foreground">
						{missed.toLocaleString()}
					</span>{" "}
					of the group-matter corrections are cases where the system produced no
					matter at all and the attorney supplied one — a miss rather than a
					wrong guess.
				</p>
			)}
		</Card>
	);
}
